import { useAtom } from 'jotai';
import { userAtom, accessTokenAtom } from '../../../../atoms';
import { Navigate } from 'react-router-dom';
import { paths } from '../../../../../config/paths';
import { useCallback, useEffect, useState, useMemo, useRef } from 'react';
import { ForceGraph2D } from 'react-force-graph';

interface RewardTreeSummary {
    id: string;
    child_number: number;
    amount: number;
    createdAt: string;
}

interface UserSummary {
    id: string;
    username: string;
    email: string;
    parent_user_id: string | null;
    user_fullpath: string;
    depth: number;
    is_debit_card_purchased: boolean;
    createdAt: string;
    reward_trees: RewardTreeSummary[];
    link_type: string | null;
}

interface GetUsersSummaryResponse {
    users: UserSummary[];
}
interface GraphNode {
    id: string;
    username: string;
    email: string;
    depth: number;
    is_debit_card_purchased: boolean;
    isRoot?: boolean;
    link_type?: string | null;
    x?: number;
    y?: number;
}

interface GraphLink {
    source: string;
    target: string;
    color: string;
}

interface SelectedNodeInfo {
    node: GraphNode;
    totalDescendants: number;
    directReferralChildren: number;
    distributorCodeUsersWithin7Levels: number;
}

interface GraphData {
    nodes: GraphNode[];
    links: GraphLink[];
}

const createGraphData = (users: UserSummary[]): GraphData => {
    // 仮想ルートノードを作成
    const rootNode: GraphNode = {
        id: 'root',
        username: 'Root',
        email: '',
        depth: 0,
        is_debit_card_purchased: false,
        isRoot: true
    };

    // ユーザーノードを作成（まずdepthで並び替え、次にリンクタイプ、最後にuser_fullpathで並び替え）
    const userNodes = [...users]
        .sort((a, b) => {
            // まずdepthで並び替え
            if (a.depth !== b.depth) {
                return a.depth - b.depth;
            }

            // depthが同じ場合はリンクタイプで並び替え
            // リンクタイプの優先順位: distributor_code > referral_code > direct > null
            const getLinkTypePriority = (linkType: string | null): number => {
                switch (linkType) {
                    case 'distributor_code': return 1;
                    case 'referral_code': return 2;
                    case 'direct': return 3;
                    default: return 4;
                }
            };

            const aPriority = getLinkTypePriority(a.link_type);
            const bPriority = getLinkTypePriority(b.link_type);

            if (aPriority !== bPriority) {
                return aPriority - bPriority;
            }

            // リンクタイプも同じ場合はuser_fullpathで並び替え
            return a.user_fullpath.localeCompare(b.user_fullpath);
        })
        .map(user => ({
            id: user.id,
            username: user.username,
            email: user.email,
            depth: user.depth, // 実際の深さをそのまま使用
            is_debit_card_purchased: user.is_debit_card_purchased,
            link_type: user.link_type,
        }));

    console.log(userNodes);

    // 全てのノードを結合(ルートノードを先頭に)
    const nodes = [rootNode, ...userNodes];

    // 既存の親子関係のリンクを作成
    const parentChildLinks = users
        .filter(user => user.parent_user_id)
        .map(user => ({
            source: user.parent_user_id!,
            target: user.id,
            color: '#000000'
        }));

    // parent_user_idがnullのユーザーをルートノードに接続
    const rootLinks = users
        .filter(user => !user.parent_user_id)
        .map(user => ({
            source: 'root',
            target: user.id,
            color: '#666666' // ルートからの接続は薄い色で表示
        }));

    // 全てのリンクを結合
    const links = [...parentChildLinks, ...rootLinks];

    return { nodes, links };
};

const useUsersSummary = () => {
    const [user] = useAtom(userAtom);
    const [accessToken] = useAtom(accessTokenAtom);
    const [isLoading, setIsLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [data, setData] = useState<GetUsersSummaryResponse | null>(null);

    const fetchSummary = useCallback(async () => {
        if (!user || !accessToken) {
            setError('認証情報が見つかりません');
            return;
        }

        setIsLoading(true);
        setError(null);

        try {
            const response = await fetch('/api/v1/admin/summary', {
                headers: {
                    'Authorization': `Bearer ${accessToken}`,
                },
            });

            if (!response.ok) {
                throw new Error('データの取得に失敗しました');
            }

            const responseData: GetUsersSummaryResponse = await response.json();
            setData(responseData);
        } catch (err) {
            setError(err instanceof Error ? err.message : '予期せぬエラーが発生しました');
        } finally {
            setIsLoading(false);
        }
    }, [user, accessToken]);

    useEffect(() => {
        fetchSummary();
    }, [fetchSummary]);

    return {
        data,
        isLoading,
        error,
        refetch: fetchSummary,
    };
};

export const SummaryPage = () => {
    const [user] = useAtom(userAtom);
    const { data, isLoading, error } = useUsersSummary();
    const [graphSize, setGraphSize] = useState({ width: 0, height: 0 });
    const [selectedNodeInfo, setSelectedNodeInfo] = useState<SelectedNodeInfo | null>(null);
    const graphContainerRef = useRef<HTMLDivElement>(null);

    useEffect(() => {
        const updateGraphSize = () => {
            if (graphContainerRef.current) {
                const rect = graphContainerRef.current.getBoundingClientRect();
                setGraphSize({
                    width: rect.width,
                    height: rect.height
                });
            }
        };

        updateGraphSize();
        window.addEventListener('resize', updateGraphSize);
        return () => window.removeEventListener('resize', updateGraphSize);
    }, []);

    const graphData = useMemo<GraphData>(() => {
        if (!data?.users) return { nodes: [], links: [] };
        return createGraphData(data.users);
    }, [data]);

    // 深さごとのユーザー数を集計
    const { depthStats, totalUsers, verifiedUsers } = useMemo(() => {
        if (!data?.users) return { depthStats: [], totalUsers: 0, verifiedUsers: 0 };

        const stats = new Map<number, number>();
        let maxDepth = 0;
        let verifiedCount = 0;

        // 各ユーザーの実際の深さで集計(深さ0は除外)
        data.users.forEach(user => {
            stats.set(user.depth, (stats.get(user.depth) || 0) + 1);
            maxDepth = Math.max(maxDepth, user.depth);
            if (user.is_debit_card_purchased) {
                verifiedCount++;
            }
        });

        // 深さ1から最大深さまでの統計を返す
        const depthStats = Array.from({ length: maxDepth }, (_, i) => ({
            depth: i + 1,
            count: stats.get(i + 1) || 0,
        }));

        return {
            depthStats,
            totalUsers: data.users.length,
            verifiedUsers: verifiedCount
        };
    }, [data]);

    if (!user?.has_admin_role) {
        return <Navigate to={paths.dashboard.path} replace />;
    }

    return (
        <div className="max-w-[1600px] mx-auto">
            <h1 className="text-2xl font-bold mb-4">ユーザーネットワーク分析</h1>
            <div className="bg-gray-50 p-4 rounded-lg mb-6">
                <h2 className="font-semibold mb-2">見方</h2>
                <ul className="space-y-1 text-sm">
                    <li className="flex items-center">
                        <div className="w-3 h-3 rounded-full bg-[#1E40AF] mr-2"></div>
                        <span>青色 → 自分自身</span>
                    </li>
                    <li className="flex items-center">
                        <div className="w-3 h-3 rounded-full bg-[#4CAF50] mr-2"></div>
                        <span>緑色 → カード代金受領確認済み</span>
                    </li>
                    <li className="flex items-center">
                        <div className="w-3 h-3 rounded-full bg-[#9E9E9E] mr-2"></div>
                        <span>灰色 → カード代金未払い</span>
                    </li>
                    <li className="flex items-center">
                        <div className="w-3 h-3 border-2 border-[#FF0000] rounded-full mr-2"></div>
                        <span>赤枠 → 代理店リンクで登録</span>
                    </li>
                </ul>
            </div>
            {selectedNodeInfo && (
                <div className="mt-4 p-4 bg-blue-50 rounded-lg">
                    <h3 className="font-semibold mb-2">選択中のユーザー情報</h3>
                    <div className="space-y-2 text-sm">
                        <p>ユーザー名: {selectedNodeInfo.node.username}</p>
                        <p>メール: {selectedNodeInfo.node.email}</p>
                        <p>登録タイプ: <span className="font-semibold">
                            {selectedNodeInfo.node.link_type === 'direct' ? 'TOP代理店' :
                                selectedNodeInfo.node.link_type === 'referral_code' ? '直紹介リンク経由で登録' :
                                    selectedNodeInfo.node.link_type === 'distributor_code' ? '代理店リンク経由で登録' :
                                        '通常登録'}
                        </span></p>
                        <div className="border-t border-blue-200 my-2"></div>
                        <p>子孫の合計数: <span className="font-semibold">{selectedNodeInfo.totalDescendants}人</span></p>
                        <p>直紹介の直下ユーザー数: <span className="font-semibold">{selectedNodeInfo.directReferralChildren}人</span></p>
                        <p>7段以内の代理店コード登録者数: <span className="font-semibold">{selectedNodeInfo.distributorCodeUsersWithin7Levels}人</span></p>
                    </div>
                </div>
            )}
            <div className="flex flex-col md:flex-row gap-6">
                {/* 左側: ネットワーク図 (デスクトップ:70%) */}
                <div className="w-full md:w-[70%] bg-white shadow rounded-lg p-6">
                    {error && (
                        <div className="bg-red-50 text-red-700 p-4 rounded-lg mb-4">
                            {error}
                        </div>
                    )}
                    {isLoading ? (
                        <div className="flex justify-center items-center h-[600px]">
                            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-500"></div>
                        </div>
                    ) : (
                        <div className="h-[600px] w-full overflow-hidden" ref={graphContainerRef}>
                            <ForceGraph2D
                                graphData={graphData}
                                nodeLabel={(node: GraphNode) =>
                                    node.isRoot ? 'ルートノード' : `${node.username} (${node.email})`
                                }
                                nodeRelSize={4}
                                linkDirectionalArrowLength={3}
                                linkDirectionalArrowRelPos={1}
                                width={graphSize.width}
                                height={graphSize.height}
                                linkColor={(link: GraphLink) => link.color}
                                dagMode="td"
                                dagLevelDistance={50}
                                linkDirectionalParticles={1}
                                linkDirectionalParticleWidth={4}
                                nodeCanvasObject={(node: GraphNode, ctx, globalScale) => {
                                    const fontSize = 12 / globalScale;
                                    ctx.font = `${fontSize}px Sans-Serif`;
                                    const nodeSize = 4;
                                    const borderWidth = 2;

                                    // ノードの背景色を設定
                                    ctx.beginPath();
                                    ctx.fillStyle = node.isRoot
                                        ? '#000000'
                                        : node.id === user?.id
                                            ? '#1E40AF'
                                            : node.is_debit_card_purchased
                                                ? '#4CAF50'
                                                : '#9E9E9E';
                                    ctx.arc(node.x!, node.y!, nodeSize, 0, 2 * Math.PI);
                                    ctx.fill();

                                    // distributor_codeの場合は赤いボーダーを追加
                                    if (node.link_type === 'distributor_code') {
                                        ctx.beginPath();
                                        ctx.strokeStyle = '#FF0000';
                                        ctx.lineWidth = borderWidth / globalScale;
                                        ctx.arc(node.x!, node.y!, nodeSize + (borderWidth / globalScale), 0, 2 * Math.PI);
                                        ctx.stroke();
                                    }
                                }}
                                onNodeClick={(node) => {
                                    if (!data?.users || node.isRoot) {
                                        setSelectedNodeInfo(null);
                                        return;
                                    }

                                    const clickedNode = node as GraphNode;
                                    const users = data.users;

                                    // 子孫を見つける関数
                                    const findDescendants = (nodeId: string) => {
                                        return users.filter(user =>
                                            user.user_fullpath.startsWith(`${nodeId}`) ||
                                            user.user_fullpath.includes(`/${nodeId}/`) ||
                                            user.user_fullpath.endsWith(`/${nodeId}`)
                                        );
                                    };

                                    // 直下の子ノードを見つける関数
                                    const findDirectChildren = (nodeId: string) => {
                                        return users.filter(user => user.parent_user_id === nodeId);
                                    };

                                    // 子孫の合計数
                                    const descendants = findDescendants(clickedNode.id);
                                    const totalDescendants = descendants.length - 1;

                                    // 直紹介リンクかつ直下のユーザー数
                                    const directChildren = findDirectChildren(clickedNode.id);
                                    const directReferralChildren = directChildren.filter(
                                        child => (child.link_type === null || child.link_type === 'referral_code') && child.id !== clickedNode.id
                                    ).length;

                                    // 子孫の直下7段までの代理店コード登録者の合計数
                                    const distributorCodeUsersWithin7Levels = descendants.filter(desc => {
                                        const relativeDepth = desc.depth - (node as GraphNode).depth;
                                        return relativeDepth <= 7 && desc.link_type === 'distributor_code' && desc.id !== clickedNode.id;
                                    }).length;

                                    // 選択中のノード情報を更新
                                    setSelectedNodeInfo({
                                        node: clickedNode,
                                        totalDescendants,
                                        directReferralChildren,
                                        distributorCodeUsersWithin7Levels
                                    });

                                    // ノードの位置固定/解除の処理
                                    const forceNode = node as { fx?: number; fy?: number; x?: number; y?: number };
                                    if (forceNode.fx !== undefined) {
                                        forceNode.fx = undefined;
                                        forceNode.fy = undefined;
                                    } else if (typeof forceNode.x === 'number' && typeof forceNode.y === 'number') {
                                        forceNode.fx = forceNode.x;
                                        forceNode.fy = forceNode.y;
                                    }
                                }}
                            />
                        </div>
                    )}
                </div>

                {/* 右側: 深さごとの集計 (デスクトップ:30%) */}
                <div className="w-full md:w-[30%] bg-white shadow rounded-lg p-6">
                    <div className="mb-4">
                        <h2 className="text-xl font-semibold mb-1">深さごとのユーザー数</h2>
                        <div className="text-gray-600">全体: {totalUsers}人（送金確認済: {verifiedUsers}人）</div>
                    </div>
                    {isLoading ? (
                        <div className="flex justify-center items-center h-40">
                            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-500"></div>
                        </div>
                    ) : (
                        <div className="space-y-4">
                            {depthStats.map(({ depth, count }) => (
                                <div key={depth} className="flex justify-between items-center">
                                    <span className="text-gray-700">深さ {depth}</span>
                                    <div className="flex items-center gap-2">
                                        <div className="h-4 bg-blue-500 rounded" style={{
                                            width: `${Math.min(count * 3, 150)}px`
                                        }}></div>
                                        <span className="text-gray-600 min-w-[3rem] text-right">
                                            {count}人
                                        </span>
                                    </div>
                                </div>
                            ))}
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
};

export default SummaryPage;